import React, { useState, useEffect } from 'react';
import { useTodoLists, useTodos } from '../hooks/useTodos';
import { TodoItem, UpdateTodoRequest, Comment } from '../types/todo';
import { formatDateForInput } from '../utils/dateUtils';
import { ListDropdown } from './ListDropdown';
import { PriorityDropdown } from './PriorityDropdown';
import { AssigneeDropdown } from './AssigneeDropdown';
import { InlineComments } from './InlineComments';
import { CustomDatePicker } from './CustomDatePicker';
import { ReminderDateTimePicker } from './ReminderDateTimePicker';
import { useDialog } from '../contexts/DialogContext';
import { useUser } from '../contexts/UserContext';
import { todoApi } from '../api/todoApi';
import { TextInput, Textarea, Stack, Text, SimpleGrid, TagsInput, Select, Grid, Checkbox } from '@mantine/core';
import { parseTags, getAllUserTags, formatTagsForApi } from '../utils/tagUtils';
import { useKanbanConfig } from '../hooks/useKanbanConfig';
import { SubTasksList } from './SubTasksList';
import { CollapsibleSection } from './CollapsibleSection';
import { Calendar, Tag, CheckSquare, MessageSquare } from 'lucide-react';
import './EditTodoForm.css';

// Separate component for the task title input that can be rendered in modal header
export const TaskTitleInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
  isCompleted?: boolean;
  onToggleComplete?: () => void;
}> = ({ value, onChange, autoFocus, isCompleted = false, onToggleComplete }) => (
  <div className="edit-form-title-section" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
    {onToggleComplete && (
      <Checkbox
        checked={isCompleted}
        onChange={onToggleComplete}
        size="md"
        styles={{
          root: {
            flexShrink: 0,
          },
          input: {
            cursor: 'pointer',
            backgroundColor: isCompleted ? '#10b981' : 'transparent',
            borderColor: isCompleted ? '#10b981' : 'var(--app-border)',
          }
        }}
      />
    )}
    <TextInput
      placeholder="What needs to be done?"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      size="xl"
      radius="md"
      autoFocus={autoFocus}
      required
      maxLength={200}
      classNames={{
        input: 'edit-form-title-input'
      }}
      styles={{
        input: {
          fontSize: window.innerWidth >= 992 ? '20px' : '18px',
          fontWeight: 600,
          border: 'none',
          borderRadius: '6px',
          padding: '8px 12px',
          color: 'var(--app-text-primary)',
          backgroundColor: 'transparent',
          transition: 'all 0.2s ease',
          textDecoration: isCompleted ? 'line-through' : 'none',
          opacity: isCompleted ? 0.6 : 1,
        },
        root: {
          flex: 1,
        }
      }}
    />
  </div>
);

interface EditTodoFormProps {
  todo: TodoItem;
  onComplete?: () => void;
  onDataChange?: (data: UpdateTodoRequest) => void;
  renderTitleInline?: boolean; // If false, title will be managed externally
}

export const EditTodoForm: React.FC<EditTodoFormProps> = ({ 
  todo, 
  onDataChange,
  renderTitleInline = true
}) => {
  const { confirm } = useDialog();
  const { currentUser } = useUser();
  const listsHook = useTodoLists();
  const { data: allTodos = [] } = useTodos();
  const [title, setTitle] = useState(todo.title || '');
  const [description, setDescription] = useState(todo.description || '');
  const [priority, setPriority] = useState<'Low' | 'Medium' | 'High'>(todo.priority || 'Medium');
  const [assignedTo, setAssignedTo] = useState(todo.assignedTo || '');
  const [dueDate, setDueDate] = useState(todo.dueDate ? formatDateForInput(todo.dueDate) : '');
  const [reminderDateTime, setReminderDateTime] = useState<string | undefined>(todo.reminderDateTime || undefined);
  const [listId, setListId] = useState<number | undefined>(todo.listId);
  const [status, setStatus] = useState(todo.status || 'todo');
  const [tags, setTags] = useState<string[]>(parseTags(todo.tags));
  const [localComments, setLocalComments] = useState<Comment[]>(todo.comments || []);
  
  // Collapsible section state - auto-expand based on data presence
  const [isAssignmentDatesOpen, setIsAssignmentDatesOpen] = useState(!!assignedTo || !!dueDate);
  const [isTagsOpen, setIsTagsOpen] = useState(tags.length > 0);
  const [isSubTasksOpen, setIsSubTasksOpen] = useState((todo.subTasks?.length || 0) > 0);
  const [isCommentsOpen, setIsCommentsOpen] = useState(localComments.length > 0);
  
  // Get tag suggestions from all user's todos
  const tagSuggestions = getAllUserTags(allTodos);

  // Get Kanban config for the current list (for status options)
  const { data: kanbanConfig } = useKanbanConfig(listId);

  // Update local state when todo changes
  useEffect(() => {
    setLocalComments(todo.comments || []);
    setTags(parseTags(todo.tags));
    setReminderDateTime(todo.reminderDateTime || undefined);
    setStatus(todo.status || 'todo');
    
    // Auto-update collapse state based on data presence
    setIsAssignmentDatesOpen(!!assignedTo || !!dueDate);
    setIsTagsOpen(parseTags(todo.tags).length > 0);
    setIsSubTasksOpen((todo.subTasks?.length || 0) > 0);
    setIsCommentsOpen((todo.comments || []).length > 0);
  }, [todo.comments, todo.tags, todo.reminderDateTime, todo.status, todo.subTasks, assignedTo, dueDate]);

  const handleAddComment = async (text: string) => {
    if (!currentUser?.email) {
      console.error('No current user');
      return;
    }

    try {
      const newComment = await todoApi.addComment(todo.id, text, currentUser.email);
      
      // Optimistic update for immediate feedback
      setLocalComments(prev => [...prev, newComment]);
      
      // The SignalR update will overwrite this when it arrives via the useEffect
    } catch (error) {
      console.error('Failed to add comment:', error);
      throw error; // Re-throw so the Comments component can handle it
    }
  };

  // Expose current form data to parent
  const getCurrentData = (): UpdateTodoRequest => ({
    title: title.trim(),
    description: description.trim() || '',
    priority,
    assignedTo: assignedTo.trim() || '',
    dueDate: dueDate || null,
    reminderDateTime: reminderDateTime || null,
    listId: listId,
    status: status,
    tags: formatTagsForApi(tags),
  });

  // Call onDataChange whenever form data changes
  useEffect(() => {
    if (onDataChange) {
      onDataChange(getCurrentData());
    }
  }, [title, description, priority, assignedTo, dueDate, reminderDateTime, listId, status, tags, onDataChange]);

  return (
    <Stack gap="lg">
      {/* HEADER: Large Task Title - only render if inline */}
      {renderTitleInline && (
        <TaskTitleInput
          value={title}
          onChange={setTitle}
          autoFocus={window.innerWidth >= 992}
        />
      )}

      {/* Desktop: Two-column layout, Mobile: Single column */}
      <Grid gutter="md">
        {/* LEFT COLUMN - Core fields (desktop 60%, mobile 100%) */}
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Stack gap="md">
            {/* Always visible: Description - moved up */}
            <Textarea
              label="Description"
              placeholder="Add more details about this task (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              size="md"
              radius="md"
              minRows={3}
              maxRows={6}
              autosize
              maxLength={1000}
              styles={{
                label: { 
                  color: 'var(--app-text-primary)',
                  fontSize: '14px',
                  fontWeight: 500,
                  marginBottom: '8px'
                }
              }}
            />

            {/* Always visible: Priority/Status/List */}
            <SimpleGrid cols={3} spacing="sm">
              <Stack gap="xs">
                <Text size="sm" fw={500} c="var(--app-text-primary)">Priority</Text>
                <PriorityDropdown
                  value={priority}
                  onChange={setPriority}
                  id="priority-dropdown"
                />
              </Stack>

              <Stack gap="xs">
                <Text size="sm" fw={500} c="var(--app-text-primary)">Status</Text>
                <Select
                  value={status}
                  onChange={(value) => setStatus(value || 'todo')}
                  data={kanbanConfig?.columns?.map(col => ({ value: col.id, label: col.name })) || [
                    { value: 'todo', label: 'To Do' },
                    { value: 'in-progress', label: 'In Progress' },
                    { value: 'done', label: 'Done' }
                  ]}
                  placeholder="Select status"
                  size="sm"
                  radius="md"
                  styles={{
                    dropdown: { zIndex: 10000 }
                  }}
                />
              </Stack>

              <Stack gap="xs">
                <Text size="sm" fw={500} c="var(--app-text-primary)">List</Text>
                <ListDropdown
                  value={listId}
                  onChange={setListId}
                  placeholder="Select list"
                  hideAllOption={true}
                />
              </Stack>
            </SimpleGrid>
          </Stack>
        </Grid.Col>

        {/* RIGHT COLUMN - Collaboration & Details (desktop 40%, mobile 100%) */}
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Stack gap="md">
            {/* Collapsible: Assignment & Dates */}
            <CollapsibleSection
              title="Assignment & Dates"
              icon={<Calendar size={16} />}
              isOpen={isAssignmentDatesOpen}
              onToggle={() => setIsAssignmentDatesOpen(!isAssignmentDatesOpen)}
            >
              <SimpleGrid cols={2} spacing="md">
                <Stack gap="xs">
                  <Text size="sm" fw={500} c="var(--app-text-primary)">Assign to</Text>
                  <AssigneeDropdown
                    value={assignedTo === '' ? '__unassigned__' : (assignedTo || undefined)}
                    onChange={async (value) => {
                      const newAssignee = value === '__unassigned__' ? '' : (value || '');
                      // Only check if assigning to someone else
                      if (newAssignee && newAssignee !== todo.assignedTo && newAssignee !== todo.createdBy) {
                        // Find the list for this todo
                        const list = todo.listId ? listsHook.data?.find((l) => l.id === todo.listId) : null;
                        if (list) {
                          const isShared = list.isShared;
                          // Check if the assignee is the list creator OR already in sharedWith
                          const isListCreator = list.createdBy === newAssignee;
                          const alreadyShared = list.sharedWith.includes(newAssignee);
                          if (!isListCreator && !alreadyShared) {
                            confirm(
                              isShared ? 'Share List' : 'List is Private',
                              isShared
                                ? `The list "${list.name}" is shared, but not with this user. Share the list with ${newAssignee} and assign the task?`
                                : `The list "${list.name}" is private. Share the list with ${newAssignee} and assign the task?`,
                              async () => {
                                await todoApi.shareListWithUser(list.id, newAssignee, list.sharedWith, todo.createdBy);
                                setAssignedTo(newAssignee);
                              }
                            );
                            return;
                          } else {
                            setAssignedTo(newAssignee);
                          }
                        } else {
                          setAssignedTo(newAssignee);
                        }
                      } else {
                        setAssignedTo(newAssignee);
                      }
                    }}
                    placeholder="Select assignee"
                    hideAnyOption={true}
                  />
                </Stack>

                <Stack gap="xs">
                  <Text size="sm" fw={500} c="var(--app-text-primary)">Due Date</Text>
                  <CustomDatePicker
                    value={dueDate}
                    onChange={(value) => setDueDate(value)}
                    placeholder="Select due date"
                    showSnooze={true}
                  />
                </Stack>
              </SimpleGrid>

              {/* Reminder - logically grouped with dates */}
              <div style={{ marginTop: '16px' }}>
                <ReminderDateTimePicker
                  value={reminderDateTime}
                  onChange={setReminderDateTime}
                  defaultDate={dueDate}
                />
              </div>
            </CollapsibleSection>

            {/* Collapsible: Tags */}
            <CollapsibleSection
              title="Tags"
              icon={<Tag size={16} />}
              count={tags.length}
              isOpen={isTagsOpen}
              onToggle={() => setIsTagsOpen(!isTagsOpen)}
            >
              <Stack gap="xs">
                <TagsInput
                  value={tags}
                  onChange={setTags}
                  data={tagSuggestions}
                  placeholder="Add tags..."
                  clearable
                  maxTags={10}
                  acceptValueOnBlur
                  splitChars={[',', ' ', ';']}
                  maxDropdownHeight={160}
                  size="md"
                  radius="md"
                  styles={{
                    input: {
                      minHeight: '42px',
                    },
                    pill: {
                      backgroundColor: 'var(--mantine-color-blue-1)',
                      color: 'var(--mantine-color-blue-7)',
                      border: '1px solid var(--mantine-color-blue-3)',
                    }
                  }}
                />
                <Text size="xs" c="dimmed">
                  Press Enter, Tab, or use commas/spaces to add tags. Maximum 10 tags.
                </Text>
              </Stack>
            </CollapsibleSection>

            {/* Collapsible: Sub-tasks */}
            {todo.id > 0 && (
              <CollapsibleSection
                title="Sub-tasks"
                icon={<CheckSquare size={16} />}
                count={todo.subTasks?.length || 0}
                isOpen={isSubTasksOpen}
                onToggle={() => setIsSubTasksOpen(!isSubTasksOpen)}
              >
                <SubTasksList 
                  parentTodoItemId={todo.id} 
                  subTasks={todo.subTasks || []} 
                  isPublicView={false}
                />
              </CollapsibleSection>
            )}

            {/* Collapsible: Comments */}
            <CollapsibleSection
              title="Discussion"
              icon={<MessageSquare size={16} />}
              count={localComments.length}
              isOpen={isCommentsOpen}
              onToggle={() => setIsCommentsOpen(!isCommentsOpen)}
            >
              <div className="comments-section-expanded">
                <InlineComments
                  comments={localComments}
                  onAddComment={handleAddComment}
                />
              </div>
            </CollapsibleSection>
          </Stack>
        </Grid.Col>
      </Grid>
    </Stack>
  );
};
